"use client";

import { useMemo, useState } from "react";
import { Icon } from "./Icons";
import {
  EmptyState,
  PageHeader,
  StatusPill,
  addDays,
  formatDate,
  todayIso
} from "./UI";

const BLOCKING = new Set(["pending", "confirmed", "checked_in"]);

export default function AvailabilityPage({ data, actions }) {
  const [startDate, setStartDate] = useState(todayIso());
  const [daysShown, setDaysShown] = useState(14);
  const dates = useMemo(
    () =>
      Array.from({ length: daysShown }, (_, index) =>
        addDays(startDate, index)
      ),
    [startDate, daysShown]
  );

  const activeReservations = data.reservations.filter((reservation) =>
    BLOCKING.has(reservation.status)
  );
  const sellableRooms = data.rooms.filter((room) => room.status !== "inactive");

  function reservationFor(roomId, date) {
    return activeReservations.find(
      (reservation) =>
        reservation.roomId === roomId &&
        reservation.checkIn <= date &&
        reservation.checkOut > date
    );
  }

  const occupiedRoomNights = sellableRooms.reduce(
    (total, room) =>
      total +
      dates.filter((date) => reservationFor(room.id, date)).length,
    0
  );
  const availableRoomNights = sellableRooms.length * dates.length;
  const occupancy = availableRoomNights
    ? Math.round((occupiedRoomNights / availableRoomNights) * 100)
    : 0;

  const arrivals = activeReservations.filter(
    (reservation) =>
      reservation.checkIn >= dates[0] &&
      reservation.checkIn <= dates[dates.length - 1]
  ).length;

  return (
    <div className="page">
      <PageHeader
        eyebrow="Live inventory"
        title="Room availability"
        text="A simple room-by-room view. Swipe horizontally on iPhone to move through the dates."
        action={
          <button
            className="button button-primary"
            onClick={() => actions.navigate("reservations")}
          >
            <Icon name="plus" size={18} />
            Add reservation
          </button>
        }
      />

      <div className="availability-overview">
        <div className="occupancy-card card">
          <div className="occupancy-ring" style={{ "--value": `${occupancy * 3.6}deg` }}>
            <span>{occupancy}%</span>
          </div>
          <div>
            <span className="eyebrow">Occupancy</span>
            <h2>{occupiedRoomNights} occupied room-nights</h2>
            <p>
              Across {sellableRooms.length} rooms and {dates.length} days.
            </p>
          </div>
        </div>
        <div className="mini-stat card">
          <span className="mini-stat-icon purple">
            <Icon name="calendar" />
          </span>
          <div>
            <strong>{arrivals}</strong>
            <span>Arrivals in view</span>
          </div>
        </div>
        <div className="mini-stat card">
          <span className="mini-stat-icon teal">
            <Icon name="bed" />
          </span>
          <div>
            <strong>{availableRoomNights - occupiedRoomNights}</strong>
            <span>Available room-nights</span>
          </div>
        </div>
      </div>

      <div className="availability-toolbar card">
        <div className="date-navigation">
          <button
            className="icon-button"
            onClick={() => setStartDate(addDays(startDate, -7))}
            aria-label="Previous week"
          >
            <Icon name="back" />
          </button>
          <label>
            <span>Starting</span>
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </label>
          <button
            className="icon-button"
            onClick={() => setStartDate(addDays(startDate, 7))}
            aria-label="Next week"
          >
            <Icon name="chevron" />
          </button>
          <button
            className="button button-small button-ghost"
            onClick={() => setStartDate(todayIso())}
          >
            Today
          </button>
        </div>
        <div className="segmented-control days-control">
          {[7, 14, 21].map((value) => (
            <button
              className={daysShown === value ? "active" : ""}
              key={value}
              onClick={() => setDaysShown(value)}
            >
              {value} days
            </button>
          ))}
        </div>
      </div>

      {sellableRooms.length ? (
        <div className="availability-scroll card">
          <div
            className="availability-grid"
            style={{
              gridTemplateColumns: `170px repeat(${dates.length}, minmax(74px, 1fr))`
            }}
          >
            <div className="availability-corner">
              <span>Room</span>
            </div>
            {dates.map((date) => {
              const dateObject = new Date(`${date}T12:00:00`);
              const isToday = date === todayIso();
              const weekend = [0, 6].includes(dateObject.getDay());
              return (
                <div
                  className={`availability-date ${isToday ? "today" : ""} ${
                    weekend ? "weekend" : ""
                  }`}
                  key={date}
                >
                  <span>
                    {new Intl.DateTimeFormat("en", { weekday: "short" }).format(
                      dateObject
                    )}
                  </span>
                  <strong>{dateObject.getDate()}</strong>
                  <small>
                    {new Intl.DateTimeFormat("en", { month: "short" }).format(
                      dateObject
                    )}
                  </small>
                </div>
              );
            })}

            {sellableRooms.map((room) => (
              <div className="availability-row-contents" key={room.id}>
                <div className="availability-room">
                  <span
                    className="room-dot"
                    style={{ backgroundColor: room.color }}
                  />
                  <div>
                    <strong>{room.code}</strong>
                    <span>{room.name}</span>
                  </div>
                  {room.status === "maintenance" ? (
                    <StatusPill value="maintenance" />
                  ) : null}
                </div>
                {dates.map((date) => {
                  const reservation = reservationFor(room.id, date);
                  const isArrival = reservation?.checkIn === date;
                  const isDeparture = reservation?.checkOut === addDays(date, 1);
                  return (
                    <div
                      className={`availability-cell ${
                        reservation ? "occupied" : "free"
                      } ${room.status === "maintenance" ? "maintenance" : ""}`}
                      key={`${room.id}-${date}`}
                      title={
                        reservation
                          ? `${reservation.guestName} · ${reservation.status}`
                          : room.status === "maintenance"
                            ? "Room in maintenance"
                            : "Available"
                      }
                    >
                      {reservation ? (
                        <span
                          style={{
                            "--room-color": room.color,
                            borderColor: room.color
                          }}
                        >
                          <b>{reservation.guestName.split(" ")[0]}</b>
                          <small>
                            {isArrival
                              ? "Arrives"
                              : isDeparture
                                ? "Leaves"
                                : "Stay"}
                          </small>
                        </span>
                      ) : room.status === "maintenance" ? (
                        <small>Closed</small>
                      ) : (
                        <i />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <EmptyState
          icon="availability"
          title="No rooms to display"
          text="Add a room before opening the availability calendar."
          action={
            <button
              className="button button-primary"
              onClick={() => actions.navigate("rooms")}
            >
              Add rooms
            </button>
          }
        />
      )}

      <div className="availability-legend">
        <span>
          <i className="legend-free" /> Available
        </span>
        <span>
          <i className="legend-booked" /> Reserved
        </span>
        <span>
          <i className="legend-maintenance" /> Maintenance
        </span>
        <span className="legend-range">
          {formatDate(dates[0])} – {formatDate(addDays(dates.at(-1), 1))}
        </span>
      </div>
    </div>
  );
}
